import React, { useEffect, useState } from 'react';
import { Button, Card, Col, Form, OverlayTrigger, Row, Spinner, Tooltip } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { getItemFromStore } from 'helpers/utils';
import { getUsersWithDepartments } from 'services/userServices';
import { submitNomination } from 'services/recognitionService';
import UserDropdownV2 from 'components/dropdowns/CustomerServices/UserDropdownV2';

const VALUES = [
  {
    id: 1,
    name: 'Integrity',
    definition: 'Doing the right thing, even when no one is watching.',
    example: 'Reporting an error you made without being asked.'
  },
  {
    id: 2,
    name: 'Teamwork',
    definition: 'Working collaboratively to achieve shared goals.',
    example: 'Helping a colleague finish their task when yours is done.'
  },
  {
    id: 3,
    name: 'Determination',
    definition: 'Persisting through challenges to achieve results.',
    example: 'Finding a solution when the first approach did not work.'
  },
  {
    id: 4,
    name: 'Excellence',
    definition: 'Consistently striving for the highest standards.',
    example: 'Going the extra mile to ensure quality of work.'
  },
  {
    id: 5,
    name: 'Pride',
    definition: 'Taking ownership and showing pride in your work and workplace.',
    example: 'Keeping your area clean and presenting yourself professionally.'
  }
];

const INITIAL_STATE = {
  nominee: null,
  nomineeDepartment: '',
  valueIds: [],
  demonstratedDate: null,
  reason: ''
};

export default function NominationForm({ onSuccess }) {
  const [loading, setLoading] = useState(true);
  const [storeUser, setStoreUser] = useState({});
  const [nominatorDepartment, setNominatorDepartment] = useState('');
  const [form, setForm] = useState(INITIAL_STATE);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const ac = new AbortController();

    const load = async () => {
      let user = getItemFromStore('user', {}, sessionStorage);

      if (!user?.userId) {
        await new Promise(r => setTimeout(r, 500));
        user = getItemFromStore('user', {}, sessionStorage);
      }

      setStoreUser(user);

      if (!user?.userId) {
        setLoading(false);
        return;
      }

      try {
        const users = await getUsersWithDepartments(ac.signal);
        const match = users.find(u => u.userId === user.userId);
        if (match?.departmentName) setNominatorDepartment(match.departmentName);
      } catch {
        // ignored — department will remain empty
      }

      setLoading(false);
    };

    load();
    return () => ac.abort();
  }, []);

  const toggleValue = (id) => {
    setForm(prev => ({
      ...prev,
      valueIds: prev.valueIds.includes(id)
        ? prev.valueIds.filter(v => v !== id)
        : [...prev.valueIds, id]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.nominee) { toast.error('Please select a nominee.'); return; }
    if (form.nominee.id === storeUser?.userId) { toast.error('You cannot nominate yourself.'); return; }
    if (form.valueIds.length === 0) { toast.error('Please select at least one value.'); return; }
    if (!form.demonstratedDate) { toast.error('Please select the date this was demonstrated.'); return; }
    if (!form.reason.trim()) { toast.error('Please provide a reason for the nomination.'); return; }

    setSubmitting(true);
    try {
      const payload = {
        nomineeId: form.nominee.id,
        valueIds: form.valueIds,
        demonstratedDate: dayjs(form.demonstratedDate).format('YYYY-MM-DD'),
        reason: form.reason.trim()
      };
      if (!form.nominee.department && form.nomineeDepartment?.trim()) {
        payload.nomineeDepartment = form.nomineeDepartment.trim();
      }
      const result = await submitNomination(payload);
      if (result?.success === false) {
        toast.error(result.message || 'Something went wrong. Please try again.');
        return;
      }
      onSuccess();
    } catch (err) {
      const msg = err?.response?.data?.message;
      toast.error(msg || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const yesterday = dayjs().subtract(0, 'day').toDate();

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <div className="mt-2 text-muted">Loading your details…</div>
      </div>
    );
  }

  return (
    <Form onSubmit={handleSubmit}>
      <Card className="mb-3">
        <Card.Header className="fw-semibold">Your Details</Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col md={6}>
              <Form.Label className="small text-muted mb-1">Your Name</Form.Label>
              <Form.Control readOnly value={storeUser?.userName ?? ''} className="bg-light" />
            </Col>
            <Col md={6}>
              <Form.Label className="small text-muted mb-1">Your Department</Form.Label>
              <Form.Control readOnly value={nominatorDepartment} className="bg-light" />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Header className="fw-semibold">Nominee Details</Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col md={6}>
              <Form.Label className="small text-muted mb-1">Nominee Name <span className="text-danger">*</span></Form.Label>
              <UserDropdownV2
                useDepartment
                isMulti={false}
                fieldName="nominee"
                placeholder="Search by name…"
                value={form.nominee?.id ?? null}
                handleChange={(_, option) =>
                  setForm(prev => ({
                    ...prev,
                    nominee: option ? { id: option.value, name: option.label, department: option.departmentName ?? '' } : null,
                    nomineeDepartment: ''
                  }))
                }
              />
            </Col>
            <Col md={6}>
              <Form.Label className="small text-muted mb-1">Nominee Department</Form.Label>
              {form.nominee && !form.nominee.department ? (
                <Form.Control
                  value={form.nomineeDepartment ?? ''}
                  onChange={(e) => setForm(prev => ({ ...prev, nomineeDepartment: e.target.value }))}
                  placeholder="Enter department…"
                />
              ) : (
                <Form.Control readOnly value={form.nominee?.department ?? ''} className="bg-light" />
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Header className="fw-semibold">Values Demonstrated (hover the pointer over the values box to see the definitions) <span className="text-danger">*</span></Card.Header>
        <Card.Body>
          <Row className="g-2">
            {VALUES.map(v => (
              <Col xs={12} sm={6} md={4} key={v.id}>
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip>
                      <strong>{v.name}:</strong> {v.definition}<br />
                      <em>Example: {v.example}</em>
                    </Tooltip>
                  }
                >
                  <Form.Check
                    type="checkbox"
                    id={`value-${v.id}`}
                    label={v.name}
                    checked={form.valueIds.includes(v.id)}
                    onChange={() => toggleValue(v.id)}
                    className="user-select-none"
                    style={{ cursor: 'help' }}
                  />
                </OverlayTrigger>
              </Col>
            ))}
          </Row>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Header className="fw-semibold">Date Demonstrated <span className="text-danger">*</span></Card.Header>
        <Card.Body>
          <DatePicker
            selected={form.demonstratedDate}
            onChange={(date) => setForm(prev => ({ ...prev, demonstratedDate: date }))}
            maxDate={yesterday}
            dateFormat="dd/MM/yyyy"
            placeholderText="Select date"
            className="form-control"
            wrapperClassName="d-block"
          />
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Header className="fw-semibold">Reason for Nomination <span className="text-danger">*</span></Card.Header>
        <Card.Body>
          <Form.Label className="small text-muted">
            Please describe what your colleague did that you believe went above and beyond
            their normal duties. Providing us with as much detail as you can will help us
            make a final decision on recognising your colleague.
          </Form.Label>
          <Form.Control
            as="textarea"
            rows={4}
            value={form.reason}
            onChange={(e) => setForm(prev => ({ ...prev, reason: e.target.value }))}
            placeholder="Describe what they did…"
          />
        </Card.Body>
      </Card>

      <div className="d-flex justify-content-end">
        <Button type="submit" variant="primary" disabled={submitting}>
          {submitting ? <><Spinner animation="border" size="sm" className="me-2" />Submitting…</> : 'Submit Nomination'}
        </Button>
      </div>
    </Form>
  );
}

NominationForm.propTypes = {
  onSuccess: PropTypes.func.isRequired
};
